import {ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit,} from '@angular/core';
import {ControlErrorDirective} from '../../../../shared/directive/control-error.directive';
import {InputText} from 'primeng/inputtext';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {FamilyMemberSchoolFormModel} from './form-group/family-member-school-form.model';
import {FormAddress} from '../../../../shared/components/address/form-address/form-address';
import {Select} from 'primeng/select';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {SlownikRodzaj} from '../../../../shared/enums/slownik-rodzaj.enum';
import {SlownikiApi} from '../../../../shared/services/api/slowniki.api';
import {Checkbox} from 'primeng/checkbox';
import {tap} from 'rxjs';
import {
  BeneficiaryDetailsFormModel
} from '../../../../shared/components/beneficiary/form-beneficiary-details/form-group/beneficiary-details-form.model';
import {DateUtils} from '../../../../shared/utils/date.utils';
import {InfoMessageComponent} from '../../../../shared/components/info-message/info-message.component';

@Component({
  selector: 'app-form-family-member-school',
  imports: [
    ControlErrorDirective,
    InputText,
    ReactiveFormsModule,
    FormAddress,
    Select,
    Checkbox,
    InfoMessageComponent,
  ],
  templateUrl: './form-family-member-school.component.html',
  styleUrl: './form-family-member-school.component.scss',

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFamilyMemberSchoolComponent implements OnInit {
  public formGroup = input.required<FormGroup<FamilyMemberSchoolFormModel>>();
  public daneOsobowe = input<FormGroup<BeneficiaryDetailsFormModel>>();

  readonly #slownikiApi = inject(SlownikiApi);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly rodzajeSzkol = toSignal(
    this.#slownikiApi.dajListePozycjiSlownika$({
      kodSlownika: SlownikRodzaj.SL_RODZAJ_SZKOLY_DSR,
    }),
  );

  ngOnInit(): void {
    this.#handleAvailabilityOfSchool();
    this.#subscribeToIsDaneSzkolyControl();
    this.#subscribeToDataUrodzeniaControl();
    this.#switchAvailabilityOfSchoolByAge();
  }

  #subscribeToIsDaneSzkolyControl(): void {
    this.formGroup()
      ?.controls.isDaneSzkoly.valueChanges.pipe(
        tap(() => this.#switchAvailabilityOfSchool()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #subscribeToDataUrodzeniaControl(): void {
    this.daneOsobowe()
      ?.controls.dataUrodzenia.valueChanges.pipe(
        tap(() => this.#switchAvailabilityOfSchoolByAge()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #handleAvailabilityOfSchool(): void {
    const { isDaneSzkoly, ...controls } = this.formGroup().controls;

    Object.values(controls).forEach((control) => {
      if (isDaneSzkoly.value) {
        control.enable();
      } else {
        control.reset();
        control.disable();
        control.updateValueAndValidity();
      }
    });
  }

  #switchAvailabilityOfSchool(): void {
    const isChecked = this.formGroup().controls.isDaneSzkoly.value;

    if (isChecked) {
      if (this.formGroup().controls.adres.controls.czyAdresZagraniczny.value) {
        this.formGroup().controls.adres.controls.adresZagraniczny.enable();
      } else {
        this.formGroup().controls.adres.controls.adres.enable();
      }
      this.formGroup().controls.rodzaj.enable();
      this.formGroup().controls.nazwa.enable();
      this.formGroup().controls.adres.controls.czyAdresZagraniczny.enable();
    } else {
      this.formGroup().controls.adres.reset();
      this.formGroup().controls.rodzaj.reset();
      this.formGroup().controls.nazwa.reset();
      this.formGroup().controls.adres.disable();
      this.formGroup().controls.rodzaj.disable();
      this.formGroup().controls.nazwa.disable();
    }
  }

  #switchAvailabilityOfSchoolByAge(): void {
    const age = DateUtils.calculateAge(
      this.daneOsobowe()?.controls?.dataUrodzenia.value,
    );
    if (age <= 26 && age >= 0) {
      this.formGroup().controls.isDaneSzkoly.enable();
    } else {
      this.formGroup().controls.isDaneSzkoly.reset();
      this.formGroup().controls.isDaneSzkoly.disable();
    }
  }
}
